import React, { useEffect, useState } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import "../styles/admin.css";

const EMPTY_FORM = { title: "", content: "", pinned: false };

export default function AdminNoticesPage() {
  const [notices, setNotices]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null); // null | { mode: "create" | "edit", data: {} }
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [flash, setFlash]       = useState(null); // { type: "success"|"error", msg }

  const showFlash = (type, msg) => {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 3000);
  };

  const fetchNotices = () => {
    setLoading(true);
    axios.get("/admin/notices")
      .then((res) => setNotices(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchNotices(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal({ mode: "create" });
  };

  const openEdit = (notice) => {
    setForm({ title: notice.title, content: notice.content, pinned: !!notice.pinned });
    setModal({ mode: "edit", id: notice.id });
  };

  const closeModal = () => { setModal(null); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      if (modal.mode === "create") {
        const res = await axios.post("/admin/notices", form);
        setNotices((prev) => [res.data, ...prev]);
        showFlash("success", "お知らせを作成しました");
      } else {
        const res = await axios.put(`/admin/notices/${modal.id}`, form);
        setNotices((prev) => prev.map((n) => n.id === modal.id ? res.data : n));
        showFlash("success", "お知らせを更新しました");
      }
      closeModal();
    } catch {
      showFlash("error", "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (notice) => {
    if (!window.confirm(`「${notice.title}」を削除しますか？`)) return;
    try {
      await axios.delete(`/admin/notices/${notice.id}`);
      setNotices((prev) => prev.filter((n) => n.id !== notice.id));
      showFlash("success", "削除しました");
    } catch {
      showFlash("error", "削除に失敗しました");
    }
  };

  return (
    <div className="adm-page adm-page-narrow">
      <AdminBreadcrumb items={[{ label: "お知らせ管理" }]} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">お知らせ管理</h2>
          <span className="adm-badge adm-badge--red">super_admin</span>
        </div>
        <button className="adm-btn adm-btn--primary" onClick={openCreate}>
          ＋ 新規作成
        </button>
      </div>

      {flash && (
        <div className={`adm-flash adm-flash--${flash.type}`}>
          {flash.msg}
          <button className="adm-flash__close" onClick={() => setFlash(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="adm-empty">読み込み中…</div>
      ) : (
        <div className="adm-card">
          <div className="adm-card__header">
            お知らせ一覧
            <span className="adm-card__header-sub">{notices.length} 件</span>
          </div>
          {notices.length === 0 ? (
            <div className="adm-empty">お知らせはありません</div>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>タイトル</th>
                    <th>本文（抜粋）</th>
                    <th>ピン留め</th>
                    <th>作成日</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {notices.map((n) => (
                    <tr key={n.id}>
                      <td className="adm-table__id">{n.id}</td>
                      <td className="adm-table__bold" style={{ maxWidth: "160px" }}>
                        {n.title}
                      </td>
                      <td className="adm-table__mute adm-table__ellipsis">
                        {n.content}
                      </td>
                      <td className="adm-center">
                        {n.pinned
                          ? <span className="adm-badge adm-badge--amber">ピン</span>
                          : <span style={{ color: "var(--adm-text-muted)", fontSize: ".75rem" }}>—</span>
                        }
                      </td>
                      <td className="adm-table__mute adm-nowrap" style={{ fontSize: ".75rem" }}>
                        {n.created_at?.slice(0, 10)}
                      </td>
                      <td>
                        <div className="adm-gap-sm">
                          <button
                            className="adm-btn adm-btn--default adm-btn--sm"
                            onClick={() => openEdit(n)}
                          >
                            編集
                          </button>
                          <button
                            className="adm-btn adm-btn--danger adm-btn--sm"
                            onClick={() => handleDelete(n)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* モーダル */}
      {modal && (
        <div className="adm-overlay" onClick={closeModal}>
          <div className="adm-modal" style={{ width: "520px" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="adm-modal__title">
              {modal.mode === "create" ? "お知らせ新規作成" : "お知らせ編集"}
            </h3>

            <div className="adm-form-field">
              <label className="adm-form-label">
                タイトル <span className="adm-form-required">*</span>
              </label>
              <input
                className="adm-input adm-input--full"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                maxLength={255}
                placeholder="タイトルを入力"
              />
            </div>

            <div className="adm-form-field">
              <label className="adm-form-label">
                本文 <span className="adm-form-required">*</span>
              </label>
              <textarea
                className="adm-input adm-input--full"
                value={form.content}
                onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                rows={6}
                placeholder="本文を入力"
                style={{ resize: "vertical", lineHeight: "1.6" }}
              />
            </div>

            <div className="adm-form-field" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                id="pinned-check"
                checked={form.pinned}
                onChange={(e) => setForm((p) => ({ ...p, pinned: e.target.checked }))}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              <label htmlFor="pinned-check" style={{ fontSize: ".8125rem", cursor: "pointer", color: "var(--adm-text-secondary)" }}>
                ピン留めする（トップに固定表示）
              </label>
            </div>

            <div className="adm-modal__actions">
              <button className="adm-btn adm-btn--default" onClick={closeModal} disabled={saving}>
                キャンセル
              </button>
              <button
                className="adm-btn adm-btn--primary"
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.content.trim()}
              >
                {saving ? "保存中…" : modal.mode === "create" ? "作成" : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
